import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '@shared/ui/LoadingSpinner';

interface ShortUrlResponse {
  success: boolean;
  data?: {
    redirect_url?: string;
    share_token?: string;
  };
  error?: string;
}

const ReportShortUrlPage: React.FC = () => {
  const { shortId } = useParams<{ shortId: string }>();
  const navigate = useNavigate();

  const { data, isLoading, error, isFetched, isError } = useQuery<ShortUrlResponse>(
    ['report-short-url', shortId],
    async () => {
      const response = await api.get(`/public/reports/short/${shortId}`);
      return response.data;
    },
    {
      enabled: !!shortId,
      retry: false,
      refetchOnWindowFocus: false,
      // Always resolve short links against the server — stale cached success would
      // send users to reports that may have been disabled (e.g. votes gating).
      staleTime: 0,
      cacheTime: 0,
    }
  );

  const shareToken = data?.success ? data.data?.share_token : undefined;
  const redirectUrl = data?.success ? data.data?.redirect_url : undefined;

  const redirectPath = (() => {
    if (shareToken) {
      return `/public/reports/${shareToken}${window.location.search}`;
    }
    if (redirectUrl) {
      try {
        const url = new URL(redirectUrl);
        const incoming = new URLSearchParams(window.location.search);
        incoming.forEach((value, key) => {
          if (!url.searchParams.has(key)) url.searchParams.set(key, value);
        });
        return url.pathname + url.search;
      } catch {
        return null;
      }
    }
    return null;
  })();

  const isLinkUnavailable = isFetched && (isError || Boolean(error) || !redirectPath);

  useEffect(() => {
    if (redirectPath) {
      navigate(redirectPath, { replace: true });
      return;
    }

    if (redirectUrl && data?.success) {
      window.location.replace(redirectUrl);
      return;
    }

    if (isLinkUnavailable) {
      toast.error('Report link not found or no longer active');
    }
  }, [redirectPath, redirectUrl, data?.success, isLinkUnavailable, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background dark:bg-background-dark-primary">
        <div className="text-center">
          <LoadingSpinner size="xl" className="mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-2">
            Loading Report...
          </h2>
          <p className="text-text-secondary dark:text-text-dark-secondary">
            Redirecting you to the report page
          </p>
        </div>
      </div>
    );
  }

  if (isLinkUnavailable) {
    const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error
      || data?.error
      || 'This report link is no longer active or does not exist.';

    return (
      <div className="min-h-screen flex items-center justify-center bg-background dark:bg-background-dark-primary">
        <div className="text-center max-w-md px-6">
          <div className="text-error-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-text-primary dark:text-text-dark-primary mb-4">
            Report Not Available
          </h2>
          <p className="text-text-secondary dark:text-text-dark-secondary mb-6">
            {message}
          </p>
          <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary mb-6">
            Votes reports are available to subscribers in the app only and can no longer be shared publicly.
          </p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background dark:bg-background-dark-primary">
      <div className="text-center">
        <LoadingSpinner size="xl" className="mx-auto mb-4" />
        <h2 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-2">
          Redirecting...
        </h2>
      </div>
    </div>
  );
};

export default ReportShortUrlPage;
